import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
// Csak típusként importálunk a backend entityből (a value import a typeorm-ot
// is behúzná a frontend bundle-be).
import type { EmploymentType, User } from '../../../../../backend/src/users/user.entity';
import { Role } from '../../../../../backend/src/users/role.entity';
import { NotificationService } from '../../notification/notification.service';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { UserDirectoryService } from '../user-directory.service';
import { BORIS_MENU, FARMY_MENU, CHAT_MENU, FOXI_MENU, defaultMenusForNewUser, initialsOf, NAV_MENU_ITEMS, normalizeMenus, roleColor } from '../user-ui';
import { AuthService } from '../../auth/auth.service';
import { roleNamesOf } from '../../auth/menu-access';

/**
 * Backend jelszó szabály (users.service.ts passwordRegex):
 * min. 8 karakter, kis- és nagybetű, szám és speciális karakter (@$!%*?&).
 */
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

@Component({
  selector: 'app-edit-user',
  templateUrl: './edit-user.component.html',
  styleUrl: './edit-user.component.scss'
})
export class EditUserComponent implements OnInit {

  @Input() user: User | null = null;
  @Input() roles: Role[] = [];
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  /** Újonnan kiválasztott profilkép (még nincs feltöltve mentésig). */
  selectedFile: File | null = null;
  /** Object URL a kiválasztott kép előnézetéhez. */
  previewUrl: string | null = null;
  /** Aktív-e a drag-and-drop rávezetés. */
  dragActive = false;

  isNew = true;
  username = '';
  fullName = '';
  email = '';
  active = true;
  /** Foglalkoztatási típus — új usernél alapértelmezés: Alkalmazott. */
  employmentType: EmploymentType = 'employee';
  selectedRoleIds: number[] = [];
  menus: { [menu: string]: boolean } = {};
  password = '';
  changingPw = false;
  pwVisible = false;
  isSaving = false;

  navMenus = NAV_MENU_ITEMS;

  /** Foglalkoztatás választó (Alkalmazott / Külsős). */
  readonly employmentChips: { value: EmploymentType; label: string }[] = [
    { value: 'employee', label: 'Alkalmazott' },
    { value: 'external', label: 'Külsős' },
  ];

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private confirmDialog: ConfirmDialogService,
    private userDirectory: UserDirectoryService,
    private authService: AuthService,
  ) { }

  ngOnInit() {
    this.isNew = !this.user?.id;
    if (this.user) {
      this.username = this.user.username || '';
      this.fullName = this.user.full_name || '';
      this.email = this.user.email || '';
      this.active = this.user.enabled === 1;
      this.employmentType = this.user.employment_type === 'external' ? 'external' : 'employee';
      this.selectedRoleIds = (this.user.roles || [])
        .map(ur => ur?.role?.id)
        .filter((id): id is number => id != null);
      this.menus = normalizeMenus((this.user as any).menus, roleNamesOf(this.user).includes('admin'));
      this.changingPw = false;
    } else {
      this.menus = defaultMenusForNewUser();
      this.changingPw = true;
      // Prototípus alapértelmezés új usernél: Csomagolás szerep, ha létezik.
      const def = this.roles.find(r =>
        (r.description || '').trim().toLowerCase().startsWith('csomagol')
        || (r.name || '').trim().toLowerCase().startsWith('csomagol'));
      if (def?.id != null) {
        this.selectedRoleIds = [def.id];
      }
    }
  }

  // --- Fejléc / avatar --------------------------------------------------

  get title(): string {
    return this.isNew ? 'Új felhasználó' : 'Felhasználó szerkesztése';
  }

  get initials(): string {
    return initialsOf(this.fullName || this.username);
  }

  get avatarColor(): string {
    const first = this.roles.find(r => r.id != null && this.selectedRoleIds.includes(r.id));
    return roleColor(first?.description || first?.name) || '#9AA098';
  }

  /** A már mentett (szerveren tárolt) profilkép fájlneve, ha van. */
  get existingImage(): string | null {
    return (this.user as any)?.image_file || null;
  }

  triggerFilePick() {
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.setFile(file);
    }
    // Reset, hogy ugyanaz a fájl újra kiválasztható legyen.
    input.value = '';
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.dragActive = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.dragActive = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.dragActive = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) {
      this.setFile(file);
    }
  }

  private setFile(file: File) {
    if (!file.type.startsWith('image/')) {
      this.notificationService.show('Csak képfájl tölthető fel.', 'danger');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.notificationService.show('A kép legfeljebb 10 MB lehet.', 'danger');
      return;
    }
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
    this.selectedFile = file;
    this.previewUrl = URL.createObjectURL(file);
  }

  clearSelectedImage() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
    this.selectedFile = null;
    this.previewUrl = null;
  }

  // --- Szerepkörök --------------------------------------------------------

  isRoleSelected(role: Role): boolean {
    return role.id != null && this.selectedRoleIds.includes(role.id);
  }

  toggleRole(role: Role) {
    if (role.id == null) {
      return;
    }
    if (this.selectedRoleIds.includes(role.id)) {
      this.selectedRoleIds = this.selectedRoleIds.filter(id => id !== role.id);
    } else {
      this.selectedRoleIds = [...this.selectedRoleIds, role.id];
    }
  }

  roleLabel(role: Role): string {
    return role.description || role.name || '';
  }

  roleChipColor(role: Role): string | null {
    return roleColor(role.description) || roleColor(role.name);
  }

  // --- Foglalkoztatási típus -------------------------------------------------

  setEmploymentType(value: EmploymentType) {
    this.employmentType = value;
  }

  get employmentHint(): string {
    return this.employmentType === 'external'
      ? 'Külsősnél nincs szabadság- és betegszabadság-elszámolás.'
      : 'Alkalmazottnál jár szabadság- és betegszabadság-elszámolás.';
  }

  // --- Menü-jogosultságok --------------------------------------------------

  isMenuOn(menu: string): boolean {
    return this.menus[menu] !== false;
  }

  /** Csapatchat-tag: részt vesz a Fő chatben, írni lehet neki, push-t kap. */
  get chatMember(): boolean {
    return this.menus[CHAT_MENU] === true;
  }

  toggleChat() {
    this.menus = { ...this.menus, [CHAT_MENU]: !this.chatMember };
  }

  /** Foxi oldala (időzített feladatok, emlékek, költés) — Foxival chatelni enélkül is lehet. */
  get foxiAccess(): boolean {
    return this.menus[FOXI_MENU] === true;
  }

  toggleFoxi() {
    this.menus = { ...this.menus, [FOXI_MENU]: !this.foxiAccess };
  }

  /** Boris oldala (tudástár, tervezőgép) — Borisszal chatelni enélkül is lehet. */
  get borisAccess(): boolean {
    return this.menus[BORIS_MENU] === true;
  }

  toggleBoris() {
    this.menus = { ...this.menus, [BORIS_MENU]: !this.borisAccess };
  }

  /** Farmy oldala (tudástár, a mini PC állapota) — Farmyval chatelni enélkül is lehet. */
  get farmyAccess(): boolean {
    return this.menus[FARMY_MENU] === true;
  }

  toggleFarmy() {
    this.menus = { ...this.menus, [FARMY_MENU]: !this.farmyAccess };
  }

  toggleMenu(menu: string) {
    if (this.isMenuLocked(menu)) {
      this.notificationService.show(`A saját „${menu}" jogodat nem veheted el — kizárnád magad. Ezt egy másik admin teheti meg.`, 'warning');
      return;
    }
    this.menus = { ...this.menus, [menu]: !this.isMenuOn(menu) };
  }

  /** Saját magától senki nem veheti el a Felhasználók / Beállítások oldalt (kizárná magát). */
  isMenuLocked(menu: string): boolean {
    const isSelf = !!this.user?.id && this.user.id === (this.authService.user as { id?: number } | null)?.id;
    return isSelf && (menu === 'Felhasználók' || menu === 'Beállítások') && this.isMenuOn(menu);
  }

  get menuCountLabel(): string {
    const on = this.navMenus.filter(m => this.isMenuOn(m)).length;
    return `${on} / ${this.navMenus.length} menüpont látható`;
  }

  // --- Jelszó ---------------------------------------------------------------

  get pwHeading(): string {
    return this.isNew ? 'Kezdeti jelszó' : 'Jelszó';
  }

  get pwEditing(): boolean {
    return this.isNew || this.changingPw;
  }

  get pwOk(): boolean {
    return PASSWORD_REGEX.test(this.password);
  }

  get pwHint(): string {
    if (this.password.length === 0) {
      return 'Add meg az új jelszót (min. 8 karakter).';
    }
    if (this.password.length < 8) {
      return 'Túl rövid — legalább 8 karakter kell.';
    }
    if (!this.pwOk) {
      return 'Kell kisbetű, nagybetű, szám és speciális karakter (@$!%*?&) is.';
    }
    return 'Megfelelő jelszó.';
  }

  get pwHintColor(): string {
    if (this.password.length === 0) {
      return '#9AA098';
    }
    return this.pwOk ? '#5C7A2E' : '#A14D4D';
  }

  startPwChange() {
    this.changingPw = true;
    this.pwVisible = false;
    this.password = '';
  }

  togglePwVisibility() {
    this.pwVisible = !this.pwVisible;
  }

  generatePassword() {
    const lower = 'abcdefghijkmnpqrstuvwxyz';
    const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const digits = '23456789';
    const special = '@$!%*?&';
    const all = lower + upper + digits + special;
    const pick = (set: string) => set[Math.floor(Math.random() * set.length)];
    const chars = [pick(lower), pick(upper), pick(digits), pick(special)];
    while (chars.length < 12) {
      chars.push(pick(all));
    }
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    this.password = chars.join('');
    this.pwVisible = true;
  }

  // --- Mentés -----------------------------------------------------------------

  get canSave(): boolean {
    return this.fullName.trim().length > 0
      && this.username.trim().length >= 3
      && (!this.pwEditing || this.pwOk);
  }

  private userRoleId(roleId: number): number | null {
    const userRole = this.user?.roles?.find(ur => ur?.role?.id === roleId);
    return userRole?.id ?? null;
  }

  save() {
    if (!this.canSave || this.isSaving) {
      return;
    }
    const payload: any = {
      id: this.user?.id ?? null,
      username: this.username.trim(),
      full_name: this.fullName.trim(),
      email: this.email.trim(),
      enabled: this.active ? 1 : 0,
      employment_type: this.employmentType,
      roles: this.roles
        .filter(r => this.isRoleSelected(r))
        .map(r => ({
          id: this.userRoleId(r.id as number),
          role_id: r.id,
          user_id: this.user?.id ?? null,
        })),
      menus: { ...this.menus },
    };
    if (this.pwEditing && this.pwOk) {
      payload.password = this.password;
    }
    this.isSaving = true;
    const url = this.user?.id ? 'users/update' : 'users/create';
    this.http.post<any>(url, payload).subscribe(res => {
      // Új usernél a create végpont visszaadja az új id-t, azzal töltjük fel a képet.
      const userId = this.user?.id ?? res?.id;
      if (this.selectedFile && userId) {
        this.uploadAvatar(userId, () => this.finishSave());
      } else {
        this.finishSave();
      }
    }, e => {
      this.notificationService.show(`Hiba a felhasználó mentése során! Részletek: ${e.error?.message || e.message}`, 'danger');
      this.isSaving = false;
    });
  }

  private uploadAvatar(userId: number, done: () => void) {
    const fd = new FormData();
    fd.append('image', this.selectedFile as File);
    this.http.post(`users/${userId}/avatar`, fd).subscribe(() => {
      // A névjegyzék gyorsítótárát el kell dobni, különben a többi képernyő
      // (support, munkaidő, üzenőfal) a régi képet — vagy az iniciálékat —
      // mutatná az oldal újratöltéséig.
      this.userDirectory.invalidate();
      done();
    }, err => {
      this.notificationService.show(`A felhasználó mentve, de a kép feltöltése nem sikerült: ${err.error?.message || err.message}`, 'danger');
      done();
    });
  }

  private finishSave() {
    this.notificationService.show('Felhasználó sikeresen mentve!', 'success');
    this.saved.emit();
  }

  /**
   * Bezárás: ha van mentetlen módosítás, megkérdezzük, mentse-e — így
   * félrekattintással/véletlen bezárással nem vész el a szerkesztés.
   */
  cancel() {
    if (this.isSaving) {
      return;
    }
    if (!this.hasUnsavedChanges()) {
      this.discard();
      return;
    }
    this.confirmDialog.open({
      title: 'Mented a módosításokat?',
      message: 'A szerkesztésben mentetlen változások vannak. Mentsük őket, vagy kilépsz mentés nélkül?',
      tone: 'warning',
      confirmLabel: 'Mentés',
      cancelLabel: 'Kilépés mentés nélkül',
    }).subscribe((result) => {
      if (result?.confirmed) {
        this.save();
      } else if (result) {
        this.discard();
      }
    });
  }

  /** Bezárás mentés nélkül (a kép-előnézet felszabadításával). */
  private discard() {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
    this.closed.emit();
  }

  /** Van-e mentetlen módosítás az űrlapon? */
  private hasUnsavedChanges(): boolean {
    if (this.isNew) {
      return !!(this.username.trim() || this.fullName.trim() || this.email.trim()
        || this.password || this.selectedFile);
    }
    const user: any = this.user || {};
    const sameRoles = this.selectedRoleIds.length === (user.roles || []).length
      && this.selectedRoleIds.every((id) => (user.roles || []).some((ur: any) => ur?.role?.id === id));
    return this.username !== (user.username || '')
      || this.fullName !== (user.full_name || '')
      || this.email !== (user.email || '')
      || this.active !== (user.enabled === 1)
      || this.employmentType !== (user.employment_type === 'external' ? 'external' : 'employee')
      || !sameRoles
      || !!this.selectedFile
      || (this.pwEditing && !!this.password);
  }

  stop(event: Event) {
    event.stopPropagation();
  }
}
